import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ar from "@/i18n/ar.json";
import en from "@/i18n/en.json";

const dictionaries = { ar, en };

const I18nContext = createContext({ lang: "ar", t: (k) => k, switchLang: () => {} });

const getFromPath = (obj, path) => {
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) {
      cur = cur[p];
    } else {
      return undefined;
    }
  }
  return cur;
};

export const detectLangFromPath = (pathname) => {
  if (pathname === "/thank_you" || pathname.startsWith("/thank_you/")) return "ar";
  const seg = pathname.split("/").filter(Boolean)[0];
  if (seg === "en") return "en";
  return "ar";
};

export const I18nProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLangFromPath(location.pathname);

  const value = useMemo(() => {
    const dict = dictionaries[lang] || dictionaries.ar;
    const t = (key) => {
      const val = getFromPath(dict, key);
      return typeof val === "string" ? val : key;
    };
    const switchLang = (nextLang) => {
      if (nextLang === lang) return;
      const path = location.pathname;
      // Do not switch language on /thank_you (Spanish only)
      if (path === "/thank_you") return;
      let nextPath;
      if (path === "/" || path === "") {
        nextPath = `/${nextLang}`;
      } else {
        const parts = path.split("/").filter(Boolean);
        if (parts[0] === "ar" || parts[0] === "en") {
          parts[0] = nextLang;
          nextPath = "/" + parts.join("/");
        } else {
          nextPath = `/${nextLang}${path}`;
        }
      }
      // Preserve scroll position on language switch
      const scrollY = window.scrollY;
      navigate(nextPath, { replace: false, state: { preserveScroll: true } });
      // Restore scroll on next tick
      setTimeout(() => window.scrollTo(0, scrollY), 0);
    };
    const localizedPath = (path) => `/${lang}${path.startsWith("/") ? path : "/" + path}`;
    return { lang, t, switchLang, localizedPath };
  }, [lang, location.pathname, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
